import { useState, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Skeleton,
  Button,
  useTheme
} from "@mui/material";
import { SelectComponent } from "@/ui-component/CustomUiComponent/ReactSelect";
import {
  useGetProjectBoardsQuery,
  useGetTicketTypesQuery,
  useGetUserStoriesQuery
} from "@/services/ticket-service/ticketGeneratorApis";
import { useTicketContext } from "@/views/pages/tickets/TicketContext";
import debounce from "lodash.debounce";

interface FormValues {
  gitlabRepository: string;
  ticketType: string;
  linkedUserStory: number;
  description: string;
}
interface Props {
  submitTicketGenerator: (data: FormValues) => void;
}
/**
 * Form to configure the ticket generation process. It handles the following:
 *   - Selecting a GitLab repository
 *   - Selecting a ticket type
 *   - Optionally selecting a linked user story
 *   - Entering the context and requirements for the ticket
 *   - Submitting the form to generate the ticket and generated content will be render in another component
 *
 * @param {Function} props.submitTicketGenerator - The function to call when the form is submitted
 */
const GitLabTicketConfigurationForm: React.FC<Props> = ({ submitTicketGenerator }: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { control, watch, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      gitlabRepository: "",
      ticketType: "",
      linkedUserStory: 0,
      description: ""
    }
  });
  const theme = useTheme();

  const { setGenerateTicketPayload, isLoading, setTicketType, ticketType } = useTicketContext();
  // Watched values
  const [projectId, ticketTypeId] = watch(["gitlabRepository", "ticketType"]);

  const debouncedSearch = useMemo(() => debounce((value) => setSearchQuery(value), 500), []);

  // API queries
  const { data: projectsQuery, isLoading: isProjectsLoading } = useGetProjectBoardsQuery({
    searchName: searchQuery.trim().toLowerCase()
  });
  const ticketTypesQuery = useGetTicketTypesQuery();
  const typeName = ticketTypesQuery.data?.find((t) => t.id === ticketTypeId)?.displayName;
  const isFetchingUserstory = typeName === "Task";
  const userStoriesQuery = useGetUserStoriesQuery(Number(projectId), {
    skip: !isFetchingUserstory || !projectId
  });

  // Memoized data transformations
  const projectsOptions = useMemo(
    () =>
      (projectsQuery?.map((p) => ({ value: p.projectId, label: p.name })) || []).sort((a, b) =>
        (a.label ?? "").localeCompare(b.label ?? "")
      ),
    [projectsQuery]
  );

  const issueOptions = useMemo(
    () =>
      (userStoriesQuery.data?.map((s) => ({ value: s.iid, label: s.title })) || []).sort((a, b) =>
        (a.label ?? "").localeCompare(b.label ?? "")
      ),
    [userStoriesQuery.data]
  );

  const selectedTicketType = useMemo(
    () => ticketTypesQuery.data?.find((t) => t.id === ticketTypeId),
    [ticketTypesQuery.data, ticketTypeId]
  );

  const onSubmit = (data: FormValues) => {
    submitTicketGenerator(data);
    setGenerateTicketPayload(data);
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto"
      }}
    >
      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box mb={4}>
            <Typography
              variant="h5"
              fontWeight={600}
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              <span
                style={{
                  color: theme.palette.primary.main,
                  fontWeight: 600
                }}
              >
                Ticket Configuration
              </span>
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure your ticket details and let AI generate the content
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Repository Select */}
              <Controller
                name="gitlabRepository"
                control={control}
                rules={{ required: "Repository is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth>
                    <FormLabel required sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>
                      GitLab Repository
                    </FormLabel>
                    {isProjectsLoading ? (
                      <Skeleton variant="rounded" height={56} />
                    ) : (
                      <SelectComponent
                        {...field}
                        options={projectsOptions}
                        placeholder="Select a Repository"
                        isMulti={false}
                        isLoading={isProjectsLoading}
                        value={projectsOptions?.find((opt) => opt.value === Number(field.value))}
                        onChange={(options) => {
                          field.onChange(options?.value);
                        }}
                        onInputChange={(value) => debouncedSearch(value)}
                      />
                    )}
                    {error && (
                      <FormHelperText
                        error
                        sx={{
                          mt: 1,
                          fontSize: "1rem"
                        }}
                      >
                        {" "}
                        {error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {/* Ticket Type Radio Group */}
              <Controller
                name="ticketType"
                control={control}
                rules={{ required: "Ticket type is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth component="fieldset" error={!!error}>
                    <FormLabel
                      required
                      component="legend"
                      sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}
                    >
                      Ticket Type
                    </FormLabel>
                    {ticketTypesQuery.isLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          gap: "2rem"
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "1rem"
                          }}
                        >
                          <Skeleton
                            animation="wave"
                            variant="circular"
                            width="20px"
                            height="20px"
                          />
                          <Skeleton
                            animation="wave"
                            variant="rectangular"
                            width="40px"
                            height="20px"
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "1rem"
                          }}
                        >
                          <Skeleton
                            animation="wave"
                            variant="circular"
                            width="20px"
                            height="20px"
                          />
                          <Skeleton
                            animation="wave"
                            variant="rectangular"
                            width="40px"
                            height="20px"
                          />
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <RadioGroup {...field} row sx={{ flexWrap: "wrap" }} >
                          {ticketTypesQuery.data?.map((ticketType) => (
                            <FormControlLabel
                              key={ticketType.id}
                              value={ticketType.id}
                              control={<Radio color="primary" />}
                              label={ticketType.displayName}
                              sx={{ mr: 3, mb: 1 }}
                              onChange={() => setTicketType(ticketType?.systemName as string)}
                            />
                          ))}
                        </RadioGroup>
                        {error && (
                          <FormHelperText sx={{ mt: -1, fontSize: "1rem" }}>
                            {error.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  </FormControl>
                )}
              />

              {/* Conditional User Story Select */}
              {isFetchingUserstory && (
                <Controller
                  name="linkedUserStory"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>
                        Related User Story
                      </FormLabel>
                      {userStoriesQuery.isLoading ? (
                        <Skeleton variant="rounded" height={56} />
                      ) : (
                        <SelectComponent
                          {...field}
                          options={issueOptions}
                          isMulti={false}
                          isLoading={userStoriesQuery.isLoading}
                          placeholder="Select a user story"
                          value={issueOptions?.find((opt) => opt.value === Number(field.value))}
                          onChange={(options) => {
                            field.onChange(options?.value);
                          }}
                        />
                      )}
                      {error && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {error.message}
                        </FormHelperText>
                      )}
                      <FormHelperText sx={{ mt: 1, color: "text.secondary", fontSize: "1.25rem" }}>
                        Select an existing user story to link with this task
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              )}

              {/* Context & Requirements */}
              <Controller
                name="description"
                control={control}
                rules={{
                  required: "Context is required"
                }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth>
                    <FormLabel required sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>
                      Context & Requirements
                    </FormLabel>
                    <TextField
                      {...field}
                      multiline
                      minRows={4}
                      maxRows={8}
                      fullWidth
                      variant="outlined"
                      sx={{
                        background: "none"
                      }}
                      inputProps={{
                        sx: {
                          "&::placeholder": {
                            fontSize: "14px",
                            opacity: 1
                          }
                        }
                      }}
                      placeholder={
                        selectedTicketType
                          ? `Enter context and requirements for ${selectedTicketType.displayName}`
                          : "Describe the task,bug or issues that needed to  be addressed "
                      }
                      error={!!error}
                      helperText={
                        <>
                          {error && (
                            <span style={{ color: "red", fontSize: "1rem" }}>{error.message}</span>
                          )}
                        </>
                      }
                      onKeyDown={(e) => {
                        if (e.ctrlKey && e.key === "Enter") {
                          handleSubmit(onSubmit)();
                        }
                      }}
                    />
                    <FormHelperText sx={{ mt: 1, color: "text.secondary", fontSize: "1.25rem" }}>
                      Press Ctrl + Enter to generate the ticket
                    </FormHelperText>
                  </FormControl>
                )}
              />

              {/* Submit Button */}
              <Button
                disableElevation
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
              >
                {isLoading
                  ? "Generating Ticket..."
                  : !selectedTicketType
                    ? "Generate Ticket"
                    : " Generate " + selectedTicketType.displayName}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GitLabTicketConfigurationForm;
